import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
const { 
  FiArrowLeft, FiRefreshCw, FiAlertCircle, FiCheckCircle, 
  FiCalendar, FiDatabase, FiExternalLink, FiClock, FiSearch, FiLock,
  FiShield, FiInfo, FiHash, FiLayers, FiChevronLeft
} = FiIcons;
import SafeIcon from '../../common/SafeIcon';
import LogoIcon from '../../common/LogoIcon';
import * as icalSyncService from '../../services/icalSyncService';

/**
 * Reusable Audit Card component for different iCal providers.
 */
const AuditCard = ({ title, provider, icon, url, setUrl, authKey, setAuthKey }) => {
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);

  const handleRunAudit = async () => {
    if (!url || !authKey) {
      setError(`Please provide both the ${title} iCal URL and an Auth Key`);
      return;
    }

    setLoading(true);
    setError(null);
    setReport(null);

    try {
      const response = await icalSyncService.dryRunAudit({ url, authKey });
      setReport(response);
    } catch (err) {
      setError(err.message || `Failed to perform ${title} audit`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-brand-olive/10 overflow-hidden flex flex-col h-full">
      <div className="p-6 border-b border-brand-olive/10 bg-brand-cream/30">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-brand-olive rounded-xl text-white shadow-lg shadow-brand-olive/5">
            <SafeIcon icon={icon} className="text-xl" />
          </div>
          <div>
            <h2 className="font-serif text-xl text-brand-olive uppercase tracking-widest">{title} Audit</h2>
            <p className="text-[10px] font-black uppercase tracking-widest text-brand-olive/40 mt-1">Preview {provider} synchronization</p>
          </div>
        </div>
      </div>

      <div className="p-8 space-y-6 flex-grow">
        <div className="space-y-5">
          <div className="space-y-2">
            <label className="block text-[10px] font-black uppercase tracking-widest text-brand-olive/40 ml-1">iCal URL</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <SafeIcon icon={FiExternalLink} className="text-brand-olive/30 text-sm" />
              </div>
              <input
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder={`Paste ${provider} iCal URL...`}
                className="w-full pl-11 pr-4 py-3.5 bg-brand-cream/5 border border-brand-olive/10 rounded-xl text-xs font-bold uppercase tracking-widest outline-none focus:border-brand-olive transition-all"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <label className="block text-[10px] font-black uppercase tracking-widest text-brand-olive/40 ml-1">Auth Key</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <SafeIcon icon={FiLock} className="text-brand-olive/30 text-sm" />
              </div>
              <input
                type="password"
                value={authKey}
                onChange={(e) => setAuthKey(e.target.value)}
                placeholder="Enter auth key"
                className="w-full pl-11 pr-4 py-3.5 bg-brand-cream/5 border border-brand-olive/10 rounded-xl text-xs font-bold uppercase tracking-widest outline-none focus:border-brand-olive transition-all"
              />
            </div>
          </div>
        </div>

        <button
          onClick={handleRunAudit}
          disabled={loading}
          className="w-full bg-brand-olive text-white py-4 rounded-xl hover:opacity-90 disabled:opacity-50 transition-all font-black uppercase tracking-widest flex items-center justify-center gap-2.5 shadow-lg shadow-brand-olive/5 text-[11px]"
        >
          {loading ? <SafeIcon icon={FiRefreshCw} className="animate-spin text-base" /> : <SafeIcon icon={FiSearch} className="text-base" />}
          Run {title} Dry-Run Audit
        </button>

        {error && (
          <div className="bg-red-50 border border-red-100 text-red-700 p-4 rounded-xl flex items-center gap-3 animate-in fade-in duration-300">
            <SafeIcon icon={FiAlertCircle} className="text-lg shrink-0" />
            <p className="text-[10px] font-black uppercase tracking-widest">{error}</p>
          </div>
        )}

        {report && <AuditResults report={report} />}
      </div>
    </div>
  );
};

const AuditResults = ({ report }) => {
  const potentialNewCount = report.records?.filter(r => r.status === 'new').length || 0;
  const issuesCount = report.summary?.errors?.length || 0;

  const stats = [
    { label: 'Parsed', value: report.summary?.parsed || 0, icon: FiCalendar },
    { label: 'Potential New', value: potentialNewCount, icon: FiCheckCircle },
    { label: 'Dups', value: report.summary?.duplicates || 0, icon: FiDatabase },
    { label: 'Conflicts', value: report.summary?.conflicts || 0, icon: FiAlertCircle },
    { label: 'Skipped Past', value: report.summary?.skipped_past || 0, icon: FiClock },
    { label: 'Issues', value: issuesCount, icon: FiInfo },
  ];

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-top-2 duration-500">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        {stats.map((stat) => (
          <div key={stat.label} className="bg-brand-cream/20 p-4 rounded-xl border border-brand-olive/5 shadow-sm">
            <div className="text-xl font-bold text-brand-olive leading-none mb-1.5">{stat.value}</div>
            <div className="text-[9px] font-black text-brand-olive/40 uppercase tracking-widest">{stat.label}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-brand-olive/5 overflow-hidden shadow-sm">
        <div className="overflow-x-auto max-h-60 overflow-y-auto">
          <table className="w-full text-left">
            <thead className="bg-brand-cream/50 sticky top-0 z-10 border-b border-brand-olive/10">
              <tr>
                <th className="px-4 py-3 text-[9px] font-black uppercase tracking-widest text-brand-olive/60">UID / Summary</th>
                <th className="px-4 py-3 text-[9px] font-black uppercase tracking-widest text-brand-olive/60">Dates</th>
                <th className="px-4 py-3 text-[9px] font-black uppercase tracking-widest text-brand-olive/60 text-center">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-olive/5">
              {report.records?.map((record, i) => (
                <tr key={i} className="hover:bg-brand-cream/5 transition-colors">
                  <td className="px-4 py-3 max-w-[140px]">
                    <div className="font-bold text-brand-olive text-[11px] truncate flex items-center gap-2">
                      <SafeIcon icon={FiHash} className="text-brand-olive/20" />
                      {record.uid?.split('-')[0] || 'N/A'}
                    </div>
                    <div className="text-brand-olive/40 text-[10px] uppercase font-black tracking-tighter truncate mt-0.5">{record.summary || 'Booking'}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-brand-olive/80 font-bold text-[10px]">{record.start_date}</div>
                    <div className="text-brand-olive/40 text-[10px] uppercase font-black tracking-tighter">{record.end_date}</div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-widest border ${
                      record.status === 'new' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 
                      record.status === 'conflict' ? 'bg-rose-50 text-rose-600 border-rose-100' :
                      record.status === 'skipped_past' ? 'bg-amber-50 text-amber-600 border-amber-100' :
                      record.status === 'duplicate' ? 'bg-blue-50 text-blue-600 border-blue-100' :
                      'bg-gray-50 text-gray-600 border-gray-100'
                    }`}>
                      {record.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const IcalSync = () => {
  const navigate = useNavigate();

  useEffect(() => {
    if (localStorage.getItem('isAdmin') !== 'true') {
      navigate('/admin/login');
    }
  }, [navigate]);

  // Lift state for comprehensive audit support
  const [airbnbUrl, setAirbnbUrl] = useState('');
  const [vrboUrl, setVrboUrl] = useState('');
  const [airbnbAuthKey, setAirbnbAuthKey] = useState('');
  const [vrboAuthKey, setVrboAuthKey] = useState('');
  
  const [compLoading, setCompLoading] = useState(false);
  const [compReport, setCompReport] = useState(null);
  const [compError, setCompError] = useState(null);

  const handleRunComprehensiveAudit = async () => {
    if (!airbnbUrl || !vrboUrl) {
      setCompError('Please provide both Airbnb and VRBO iCal URLs for a comprehensive audit');
      return;
    }

    // Use Airbnb auth key as primary, or fallback to VRBO
    const authKey = airbnbAuthKey || vrboAuthKey;
    if (!authKey) {
      setCompError('Auth Key is required for comprehensive audit');
      return;
    }

    setCompLoading(true);
    setCompError(null);
    setCompReport(null);

    try {
      const response = await icalSyncService.dryRunComprehensiveAudit({ 
        urls: [airbnbUrl, vrboUrl], 
        authKey 
      });
      setCompReport(response);
    } catch (err) {
      setCompError(err.message || 'Failed to perform comprehensive audit');
    } finally {
      setCompLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-cream p-6 md:p-10">
      <div className="max-w-7xl mx-auto">
        <header className="flex flex-col md:flex-row md:items-center justify-between mb-12 gap-6">
          <div className="flex items-center gap-6">
            <button 
              onClick={() => navigate('/admin')}
              className="p-3 bg-white border border-brand-olive/10 rounded-full text-brand-olive hover:bg-brand-cream transition-all shadow-sm group"
              title="Back to Dashboard"
            >
              <SafeIcon icon={FiChevronLeft} className="text-xl group-hover:-translate-x-0.5 transition-transform" />
            </button>
            <div>
              <h1 className="font-serif text-3xl text-brand-olive uppercase tracking-[0.1em]">iCal Synchronization</h1>
              <p className="text-brand-olive/60 text-[10px] font-black uppercase tracking-widest mt-1.5">Verification for Airbnb and VRBO/HomeAway feeds</p>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-amber-50 text-amber-700 px-5 py-3 rounded-full border border-amber-100 text-[10px] font-black uppercase tracking-widest shadow-sm self-start md:self-center">
            <SafeIcon icon={FiShield} className="text-sm" />
            Phase 1: Audit Mode Only (commit:false)
          </div>
        </header>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 mb-10">
          <AuditCard 
            title="Airbnb" 
            provider="Airbnb" 
            icon={FiCalendar} 
            url={airbnbUrl}
            setUrl={setAirbnbUrl}
            authKey={airbnbAuthKey}
            setAuthKey={setAirbnbAuthKey}
          />
          <AuditCard 
            title="VRBO" 
            provider="VRBO / HomeAway" 
            icon={FiExternalLink} 
            url={vrboUrl}
            setUrl={setVrboUrl}
            authKey={vrboAuthKey}
            setAuthKey={setVrboAuthKey}
          />
        </div>

        {/* Comprehensive Audit Section */}
        <div className="bg-white rounded-2xl shadow-xl border border-brand-olive/5 overflow-hidden mb-12">
          <div className="p-6 border-b border-brand-olive/10 bg-brand-olive text-white">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm shadow-inner">
                <SafeIcon icon={FiLayers} className="text-2xl" />
              </div>
              <div>
                <h2 className="font-serif text-xl uppercase tracking-widest">Cross-Channel Dry-Run Audit</h2>
                <p className="text-[10px] font-black uppercase tracking-widest text-white/60 mt-1">Detect cross-feed overlaps between Airbnb and VRBO</p>
              </div>
            </div>
          </div>
          
          <div className="p-8 space-y-8">
            <div className="bg-brand-cream/30 border border-brand-olive/5 p-5 rounded-2xl flex gap-4 items-start">
              <SafeIcon icon={FiInfo} className="text-brand-olive text-lg mt-0.5 shrink-0" />
              <p className="text-[11px] text-brand-olive/70 font-medium leading-relaxed">
                This audit parses both feeds simultaneously. It identifies potential new bookings and detects if the same block exists in multiple channels (cross-feed overlap) before any database commitment.
              </p>
            </div>

            <button
              onClick={handleRunComprehensiveAudit}
              disabled={compLoading}
              className="btn-primary w-full py-4 text-[11px] flex items-center justify-center gap-3 shadow-xl"
            >
              {compLoading ? (
                <SafeIcon icon={FiRefreshCw} className="animate-spin text-base" />
              ) : (
                <SafeIcon icon={FiLayers} className="text-base" />
              )}
              Execute Comprehensive Cross-Channel Dry-Run
            </button>

            {compError && (
              <div className="bg-red-50 border border-red-100 text-red-700 p-4 rounded-xl flex items-center gap-3">
                <SafeIcon icon={FiAlertCircle} className="text-lg shrink-0" />
                <p className="text-[10px] font-black uppercase tracking-widest">{compError}</p>
              </div>
            )}

            {compReport && (
              <div className="mt-10 pt-10 border-t border-brand-olive/10">
                <div className="mb-6">
                  <span className="px-3 py-1.5 bg-brand-olive text-white text-[9px] font-black uppercase tracking-widest rounded-md shadow-sm">Comprehensive Results</span>
                </div>
                <div className="max-w-3xl">
                  <AuditResults report={compReport} />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="text-center pb-12">
          <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-white/50 border border-brand-olive/5">
            <div className="w-1.5 h-1.5 rounded-full bg-brand-olive animate-pulse" />
            <p className="text-[10px] font-black uppercase tracking-widest text-brand-olive/40 italic">
              All requests are strictly dry-run. Database modifications are disabled.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IcalSync;